"use client"

import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"

export type LogoMarqueeItem = {
  src: string
  alt: string
  /** Display height in pixels; width scales proportionally. */
  h: number
  /** Pre-translated label shown on hover. Omit to hide the hover label. */
  label?: string
  /** Optional external URL. When set, the logo becomes a link that opens in a new tab. */
  href?: string
}

/** Static lookup so Tailwind's JIT picks up the literal `mr-*` utilities.
 *  Every value used as a `gapClassName` prop value at a call site must
 *  appear as a key here. The fallback runtime substitution is only a
 *  safety net; it does not generate Tailwind classes. */
const GAP_TO_MR: Record<string, string> = {
  "gap-10 md:gap-14": "mr-10 md:mr-14",
  "gap-20": "mr-20",
  "gap-16": "mr-16",
  "gap-12": "mr-12",
  "gap-10": "mr-10",
}

export type LogoMarqueeProps = {
  items: LogoMarqueeItem[]
  /** "light" renders grayscale logos on bg-background; "dark" renders white logos for dark backgrounds. */
  theme: "light" | "dark"
  className?: string
  /** Scroll duration in seconds. Default: 40. */
  duration?: number
  /** Tailwind gap utilities between logos. Default: "gap-10 md:gap-14". */
  gapClassName?: string
}

/**
 * Infinite horizontal logo marquee with optional per-logo hover labels
 * ("Partner with" / "As seen on" / "Experience at", supplied by the caller)
 * and optional click-through links. Slows to 1/3 speed on hover so labels
 * are readable and any links can be clicked comfortably.
 *
 * Uses the global `@keyframes scroll-marquee-25` (defined in app/globals.css)
 * paired with 4 repeated copies of the items and a per-item margin-right
 * (NOT flex `gap`). This combination produces a seam-free loop at any
 * viewport width up to 2× a single-copy width. Earlier versions used 2
 * copies + flex-gap + a -50% keyframe and visibly snapped at the end of
 * each loop on wide viewports (AHD-6405).
 */
export function LogoMarquee({
  items,
  theme,
  className,
  duration = 40,
  gapClassName = "gap-10 md:gap-14",
}: LogoMarqueeProps) {
  // `gapClassName` historically applied a flex `gap-*` utility on the rail.
  // Flex gap places spacing between siblings only, which mis-aligns the
  // wrap math by half a gap when the rail is doubled (each loop snaps back
  // by ~gap/2). We now apply the same spacing as a margin-right on every
  // child so each item carries its own trailing spacing — the rail width
  // becomes an exact multiple of copy-with-margin, and `translateX(-25%)`
  // lands on a copy boundary cleanly.
  //
  // Tailwind's JIT only sees classes that appear literally in source, so
  // we hard-code the gap → mr translation for every value callers pass.
  // Add a new entry here when adding a new `gapClassName` at a call site.
  const itemMarginClassName =
    GAP_TO_MR[gapClassName] ?? gapClassName.replace(/(^|\s|:)gap-/g, "$1mr-")
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [isHover, setIsHover] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number>(0)

  const hasLabels = items.some((i) => i.label)

  // Adjust playback rate on hover instead of swapping animation-duration
  // (swapping the duration property restarts the animation from 0%, which
  // visibly jumps the logo order). playbackRate changes speed smoothly.
  useEffect(() => {
    const rail = railRef.current
    if (!rail) return
    // `Element.getAnimations()` is unimplemented in jsdom — guard so the
    // component (and any page that renders it, e.g. /pricing) stays
    // mountable under unit tests.
    if (typeof rail.getAnimations !== "function") return
    const anim = rail.getAnimations()[0]
    if (!anim) return
    anim.playbackRate = isHover ? 1 / 3 : 1
  }, [isHover])

  function handleMouseMove(e: React.MouseEvent) {
    if (!hasLabels) return
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const container = containerRef.current
      if (!container) return
      const els = Array.from(
        container.querySelectorAll("[data-idx]")
      ) as HTMLElement[]
      for (const el of els) {
        const rect = el.getBoundingClientRect()
        if (e.clientX >= rect.left && e.clientX <= rect.right) {
          setHoveredIndex(Number(el.dataset.idx))
          return
        }
      }
      setHoveredIndex(null)
    })
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative overflow-hidden", hasLabels && "pt-6", className)}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => {
        setIsHover(false)
        setHoveredIndex(null)
      }}
      onMouseMove={handleMouseMove}
    >
      {theme === "light" && (
        <>
          <div className="from-background pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-16 bg-gradient-to-r to-transparent md:w-24" />
          <div className="from-background pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-16 bg-gradient-to-l to-transparent md:w-24" />
        </>
      )}
      <div
        ref={railRef}
        className="flex items-end"
        style={{
          animation: `scroll-marquee-25 ${duration}s linear infinite`,
          width: "max-content",
        }}
      >
        {[...items, ...items, ...items, ...items].map((logo, i) => {
          const isHovered =
            hoveredIndex !== null &&
            i % items.length === hoveredIndex % items.length
          const Wrapper = logo.href ? "a" : "div"
          const wrapperProps = logo.href
            ? {
                href: logo.href,
                target: "_blank",
                rel: "noopener noreferrer nofollow",
              }
            : {}
          return (
            <Wrapper
              key={i}
              data-idx={i}
              {...wrapperProps}
              className={cn(
                "relative flex shrink-0 flex-col items-center",
                itemMarginClassName,
                logo.href ? "cursor-pointer" : "cursor-default"
              )}
            >
              {logo.label && (
                <span
                  className={cn(
                    "type-label-sm pointer-events-none mb-2 whitespace-nowrap transition-[opacity,transform] duration-150",
                    theme === "dark" ? "text-white/60" : "text-neutral-500"
                  )}
                  style={{
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered ? "translateY(0)" : "translateY(4px)",
                  }}
                >
                  {logo.label}
                </span>
              )}
              <Image
                src={logo.src}
                alt={logo.alt}
                width={100}
                height={32}
                style={{
                  height: logo.h,
                  ...(theme === "dark"
                    ? {
                        filter: "brightness(0) invert(1)",
                        opacity: isHovered ? 0.7 : 0.4,
                      }
                    : {
                        filter: isHovered ? "grayscale(0)" : "grayscale(1)",
                        opacity: isHovered ? 1 : 0.7,
                      }),
                  transition: "opacity 0.15s, filter 0.15s",
                }}
                className="w-auto shrink-0 object-contain"
              />
            </Wrapper>
          )
        })}
      </div>
    </div>
  )
}
